import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
	Bot,
	ChevronDown,
	ChevronUp,
	Fish,
	Loader2,
	Maximize2,
	MessageSquare,
	ShieldCheck,
	X,
} from "lucide-react";
import { useMRNotes } from "../hooks/useMRNotes";
import { safeHttpUrl } from "../utils";
import { Avatar } from "./Avatar";
import { Tooltip } from "./Tooltip";
import { DateTime } from "./DateTime";

// Renders a comment body: prefers GitLab's rendered HTML (full markdown
// formatting), falls back to plain text if bodyHtml is unavailable.
const CommentBody = ({ note, className }) => {
	if (note.bodyHtml) {
		return (
			<div
				className={`rb-comment-html ${className || ""}`}
				// GitLab sanitizes bodyHtml server-side before returning it.
				dangerouslySetInnerHTML={{ __html: note.bodyHtml }}
			/>
		);
	}
	return (
		<div className={`whitespace-pre-wrap break-words ${className || ""}`}>
			{note.body}
		</div>
	);
};

const CommentSnippet = ({ label, icon, note, emptyText, onExpand }) => {
	const header = (
		<div className="flex items-center justify-between gap-1.5">
			<div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
				{icon}
				{label}
			</div>
			{note && onExpand && (
				<Tooltip content="Expand comment">
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onExpand({ note, label, icon });
						}}
						className="p-0.5 rounded text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors"
						aria-label={`Expand ${label}`}
					>
						<Maximize2 className="w-3 h-3" />
					</button>
				</Tooltip>
			)}
		</div>
	);

	if (!note) {
		return (
			<div className="space-y-1">
				{header}
				<div className="text-xs text-[var(--color-text-secondary)] italic">
					{emptyText}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-1">
			{header}
			<div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-2">
				<div className="flex items-center justify-between gap-2 mb-1">
					<span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-primary)]">
						<Avatar
							src={note.author?.avatarUrl}
							alt={note.author?.name}
							size="sm"
							className="flex-shrink-0"
						/>
						<span className="truncate max-w-[120px]">{note.author?.name}</span>
					</span>
					<span className="text-[10px] text-[var(--color-text-secondary)] whitespace-nowrap">
						<DateTime date={note.createdAt} />
					</span>
				</div>
				<CommentBody
					note={note}
					className="max-h-32 overflow-y-auto text-xs text-[var(--color-text-secondary)] leading-relaxed"
				/>
				{safeHttpUrl(note.url) && (
					<a
						href={safeHttpUrl(note.url)}
						target="_blank"
						rel="noopener noreferrer"
						draggable="false"
						onClick={(e) => e.stopPropagation()}
						className="inline-block mt-1 text-[10px] text-[var(--color-link)] hover:underline"
					>
						View on GitLab
					</a>
				)}
			</div>
		</div>
	);
};

// Small inline modal that lifts a single comment out with full formatting.
const CommentModal = ({ entry, onClose }) => {
	useEffect(() => {
		const handleKey = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [onClose]);

	if (!entry) return null;
	const { note, label, icon } = entry;

	return createPortal(
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
			onClick={onClose}
			onDragStart={(e) => e.preventDefault()}
		>
			<div
				className="bg-[var(--color-bg-primary)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-[var(--color-border)] overflow-hidden"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Modal header */}
				<div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50">
					<div className="flex flex-col gap-2 min-w-0">
						<span className="inline-flex items-center gap-1.5 self-start px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
							{icon}
							{label}
						</span>
						<div className="flex items-center gap-2 min-w-0">
							<span className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-text-primary)] min-w-0">
								<Avatar
									src={note.author?.avatarUrl}
									alt={note.author?.name}
									size="sm"
									className="flex-shrink-0"
								/>
								<span className="truncate">{note.author?.name}</span>
							</span>
							<span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
								<DateTime date={note.createdAt} />
							</span>
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="p-1 -mr-1 rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors flex-shrink-0"
						aria-label="Close"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
				{/* Modal body */}
				<div className="px-5 py-4 overflow-y-auto">
					<CommentBody
						note={note}
						className="rb-comment-html-lg text-sm text-[var(--color-text-primary)] leading-relaxed"
					/>
				</div>
				{/* Modal footer */}
				{safeHttpUrl(note.url) && (
					<div className="px-5 py-3 border-t border-[var(--color-border)] text-right">
						<a
							href={safeHttpUrl(note.url)}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-link)] hover:bg-[var(--color-bg-secondary)] transition-colors"
						>
							View on GitLab
						</a>
					</div>
				)}
			</div>
		</div>,
		document.body,
	);
};

const iconClass = "w-3.5 h-3.5 text-[var(--color-text-secondary)]";

/**
 * Collapsible "Show comments" control that lazily loads and displays the
 * latest AI reviewer, Murloc, Atlantis and human comments for a merge request.
 * Reusable across the Standard, Swimlane and Review Board card layouts.
 */
export const MRComments = ({ mrId, className = "" }) => {
	const [expanded, setExpanded] = useState(false);
	const [modalEntry, setModalEntry] = useState(null);
	const { load, loading, error, loaded, aiNote, murlocNote, atlantisNote, humanNote } =
		useMRNotes(mrId);

	useEffect(() => {
		if (expanded && !loaded && !loading) {
			load();
		}
	}, [expanded, loaded, loading, load]);

	return (
		<div className={className}>
			{/* Unfurl toggle */}
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					setExpanded((prev) => !prev);
				}}
				className="w-full inline-flex items-center justify-center gap-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] py-1 rounded hover:bg-[var(--color-bg-secondary)] transition-colors"
				aria-expanded={expanded}
			>
				{expanded ? (
					<ChevronUp className="w-3.5 h-3.5" />
				) : (
					<ChevronDown className="w-3.5 h-3.5" />
				)}
				{expanded ? "Hide comments" : "Show comments"}
			</button>

			{/* Unfurled comments section */}
			{expanded && (
				<div
					className="mt-2 space-y-2"
					draggable={false}
					onDragStart={(e) => e.stopPropagation()}
				>
					{loading && (
						<div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] py-2">
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
							Loading comments...
						</div>
					)}

					{error && !loading && (
						<div className="text-xs text-red-500 py-1">{error}</div>
					)}

					{loaded && !loading && (
						<>
							<CommentSnippet
								label="AI Review"
								icon={<Bot className={iconClass} />}
								note={aiNote}
								emptyText="No AI Reviewer comment yet."
								onExpand={setModalEntry}
							/>
							{murlocNote && (
								<CommentSnippet
									label="Murloc"
									icon={<Fish className={iconClass} />}
									note={murlocNote}
									onExpand={setModalEntry}
								/>
							)}
							{atlantisNote && (
								<CommentSnippet
									label="Atlantis (policy)"
									icon={<ShieldCheck className={iconClass} />}
									note={atlantisNote}
									onExpand={setModalEntry}
								/>
							)}
							<CommentSnippet
								label="Last human comment"
								icon={<MessageSquare className={iconClass} />}
								note={humanNote}
								emptyText="No human comments yet."
								onExpand={setModalEntry}
							/>
						</>
					)}
				</div>
			)}

			<CommentModal entry={modalEntry} onClose={() => setModalEntry(null)} />
		</div>
	);
};
